import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import CustomTable from '../../../../../components/CustomTable';
import CustomModal from '../../../../../components/CustomModal';
import CustomInput from '../../../../../components/CustomInput';
import { Space, Radio } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import {
    emailDriversLoadingStatus,
    allEmailDrivers,
    getEmailDrivers,
    createEmailDriver,
    deleteEmailDriver,
    updateEmailDriver,
} from '../../../../../redux/slices/settingsSlice';
import { toast } from 'react-toastify';

export default function EmailDriversSubPage() {
    const dispatch = useDispatch();

    const emailDriversStatus = useSelector(emailDriversLoadingStatus);
    const emailDrivers = useSelector(allEmailDrivers);

    const [isCreateDriverModalVisible, setIsCreateDriverModalVisible] =
        useState(false);
    const [isEditDriverModalVisible, setIsEditDriverModalVisible] =
        useState(false);
    const [isDeleteDriverModalVisible, setIsDeleteDriverModalVisible] =
        useState(false);
    const [radioButtonValue, setRadioButtonValue] = useState('yes');
    const [data, setData] = useState({
        name: '',
        smtp_host: '',
        smtp_username: '',
        smtp_password: '',
        smtp_port: '',
        tls: true,
    });
    const [selectedDriver, setSelectedDriver] = useState({
        id: null,
        name: '',
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getEmailDrivers());
    }, [dispatch]);

    const resetState = () => {
        setData({
            name: '',
            smtp_host: '',
            smtp_username: '',
            smtp_password: '',
            smtp_port: '',
            tls: true,
        });
        setRadioButtonValue('yes');
    };

    const toggleCreateDriverModal = () => {
        resetState();
        setErrors({});
        setIsCreateDriverModalVisible(!isCreateDriverModalVisible);
    };

    const toggleEditDriverModal = (record) => {
        if (record === undefined) {
            setIsEditDriverModalVisible(!isEditDriverModalVisible);
        } else {
            setData({
                name: record.name,
                smtp_host: record.smtp_host,
                smtp_username: record.smtp_username,
                smtp_password: record.smtp_password,
                smtp_port: record.smtp_port,
                tls: record.rls === 'Yes' ? true : false,
            });
            setRadioButtonValue(record?.tls?.toLowerCase());
            setSelectedDriver({ id: record.id, name: record.name });
            setErrors({});
            setIsEditDriverModalVisible(!isEditDriverModalVisible);
        }
    };

    const toggleDeleteDriverModal = (record) => {
        if (record === undefined) {
            setIsDeleteDriverModalVisible(!isDeleteDriverModalVisible);
        } else {
            setSelectedDriver({ id: record.id, name: record.name });
            setIsDeleteDriverModalVisible(!isDeleteDriverModalVisible);
        }
        setErrors({});
    };

    const onInputChange = (e) => {
        const value = e.target.value;
        setData({
            ...data,
            [e.target.id]: value,
        });
    };

    const onRadioButtonsChange = (e) => {
        setRadioButtonValue(e.target.value);
        setData({
            ...data,
            tls: e.target.value === 'yes' ? true : false,
        });
    };

    const onCreateDriver = async () => {
        try {
            await dispatch(createEmailDriver(data)).unwrap();
            toast.success('New email driver successfully added');
            toggleCreateDriverModal();
            dispatch(getEmailDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onEditDriver = async () => {
        try {
            await dispatch(
                updateEmailDriver({ id: selectedDriver.id, body: data }),
            ).unwrap();
            toast.success('Changes saved successfully');
            toggleEditDriverModal();
            dispatch(getEmailDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteDriver = async () => {
        try {
            await dispatch(deleteEmailDriver(selectedDriver.id)).unwrap();
            toast.success('Email driver successfully deleted');
            toggleDeleteDriverModal();
            dispatch(getEmailDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const getFilteredDrivers = (data, filter) => {
        const formatedData = data?.map((el) => {
            const newObj = { ...el };
            if (newObj.tls) {
                newObj.tls = 'Yes';
            } else {
                newObj.tls = 'No';
            }
            return newObj;
        });

        const filteredData = formatedData.filter((el) =>
            el?.name.toLowerCase().includes(filter.toLowerCase()),
        );

        return filteredData;
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined onClick={(e) => toggleEditDriverModal(record)} />
                <DeleteOutlined
                    onClick={(e) => toggleDeleteDriverModal(record)}
                />
            </Space>
        ),
    };

    return (
        <>
            <Helmet>
                <title>
                    Console Defentry - Settings - Notifications - Email drivers
                </title>
            </Helmet>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={[
                        'Name',
                        'SMTP host',
                        'SMTP username',
                        'SMTP port',
                        'TLS',
                        'Actions',
                    ]}
                    functionalCell={{ Actions: actionsOption }}
                    onAddButtonClick={toggleCreateDriverModal}
                    tablePannelTitle="List of drivers"
                    searchInputPlaceholder="Search by name"
                    topPannelButtonText="Create driver"
                    data={emailDrivers}
                    getFilteredData={getFilteredDrivers}
                    isLoading={emailDriversStatus === 'loading'}
                    withFilter
                />
            </div>
            <CustomModal
                title="Create driver"
                subText="Please fill the fields below"
                subTextAlign="left"
                isModalVisible={isCreateDriverModalVisible}
                onAcceptButtonClick={onCreateDriver}
                onCancelButtonClick={toggleCreateDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Create"
            >
                <div className="form__item-wrapper">
                    <CustomInput
                        id="name"
                        placeholder="Enter name"
                        onInputChange={onInputChange}
                        inputValue={data.name}
                        labelText="Name"
                        width="100%"
                        errorText={errors?.name}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="smtp_host"
                        placeholder="Enter SMTP host"
                        onInputChange={onInputChange}
                        inputValue={data.smtp_host}
                        labelText="SMTP host"
                        width="100%"
                        errorText={errors?.smtp_host}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="smtp_username"
                        placeholder="Enter SMTP username"
                        onInputChange={onInputChange}
                        inputValue={data.smtp_username}
                        labelText="SMTP username"
                        width="100%"
                        errorText={errors?.smtp_username}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="smtp_password"
                        placeholder="Enter SMTP password"
                        onInputChange={onInputChange}
                        inputValue={data.smtp_password}
                        labelText="SMTP password"
                        width="100%"
                        errorText={errors?.smtp_password}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="smtp_port"
                        placeholder="Enter SMTP port"
                        onInputChange={onInputChange}
                        inputValue={data.smtp_port}
                        labelText="SMTP port"
                        width="100%"
                        errorText={errors?.smtp_port}
                    />
                </div>
                <div className="form__item-wrapper--column">
                    <p className="input-label">TLS</p>
                    <Radio.Group
                        onChange={onRadioButtonsChange}
                        value={radioButtonValue}
                    >
                        <Space direction="vertical" size={'large'}>
                            <Radio value="yes">Yes</Radio>
                            <Radio value="no">No</Radio>
                        </Space>
                    </Radio.Group>
                </div>
            </CustomModal>
            <CustomModal
                title="Edit driver"
                subText="Please fill the fields below"
                subTextAlign="left"
                isModalVisible={isEditDriverModalVisible}
                onAcceptButtonClick={onEditDriver}
                onCancelButtonClick={toggleEditDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Update"
            >
                <div className="form__item-wrapper">
                    <CustomInput
                        id="name"
                        placeholder="Enter name"
                        onInputChange={onInputChange}
                        inputValue={data.name}
                        labelText="Name"
                        width="100%"
                        errorText={errors?.name}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="smtp_host"
                        placeholder="Enter SMTP host"
                        onInputChange={onInputChange}
                        inputValue={data.smtp_host}
                        labelText="SMTP host"
                        width="100%"
                        errorText={errors?.smtp_host}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="smtp_username"
                        placeholder="Enter SMTP username"
                        onInputChange={onInputChange}
                        inputValue={data.smtp_username}
                        labelText="SMTP username"
                        width="100%"
                        errorText={errors?.smtp_username}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="smtp_password"
                        placeholder="Enter SMTP password"
                        onInputChange={onInputChange}
                        inputValue={data.smtp_password}
                        labelText="SMTP password"
                        width="100%"
                        errorText={errors?.smtp_password}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="smtp_port"
                        placeholder="Enter SMTP port"
                        onInputChange={onInputChange}
                        inputValue={data.smtp_port}
                        labelText="SMTP port"
                        width="100%"
                        errorText={errors?.smtp_port}
                    />
                </div>
                <div className="form__item-wrapper--column">
                    <p className="input-label">TLS</p>
                    <Radio.Group
                        onChange={onRadioButtonsChange}
                        value={radioButtonValue}
                    >
                        <Space direction="vertical" size={'large'}>
                            <Radio value="yes">Yes</Radio>
                            <Radio value="no">No</Radio>
                        </Space>
                    </Radio.Group>
                </div>
            </CustomModal>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${selectedDriver.name} `}</span>
                        driver?
                    </p>
                }
                subText="You will delete this driver without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteDriverModalVisible}
                onAcceptButtonClick={onDeleteDriver}
                onCancelButtonClick={toggleDeleteDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            >
                {errors.email_driver_id && (
                    <p className="modal__error-text">
                        {errors.email_driver_id[0]}
                    </p>
                )}
            </CustomModal>
        </>
    );
}
